import { Button, Dropdown, message, Space, Spin, Typography } from "antd";
import MainLayout from "../components/Layout/MainLayout";
import DataTable from "../components/Tables/Datatable";
import SearchInput from "../components/Forms/SearchInput";
import { useCallback, useEffect, useRef, useState } from "react";
import { items, productsMenuItems, roleIds } from "../constant";
import { TbDotsVertical } from "react-icons/tb";
import { FaHeart } from "react-icons/fa";
import { useNavigate, useOutletContext, useSearchParams } from "react-router-dom";
import { FiEdit2 } from "react-icons/fi";
import { useApiCall } from "../services/api";
import FileUploadModal from "../components/models/FileUploadModal";
import { useSelector } from "react-redux";
import { useGlobalModal } from "../context/SuccessModalProvider";
import Loader from "../common/Loader";
import Filters from "../components/Filters";
import ProductImageName from "../components/Layout/ProductImageName";
import { calcInStock, getStockStatus, handleRouteNav, inchesToCm } from "../utils";
import CustomBadge from "../components/Charts/CustomBadge";
import PrimaryButton from "../components/Forms/PrimaryButton";
import BrocuresModal from "../components/models/BrocuresModal";
import usePagination from "../hooks/usePagination";
import ProductLinkModal from "../components/models/ProductLinkModal";

export const ProductMorePagesButtons = ({ activeButton, role_id, isInternalPage = true, openBrochureModal }) => {
  const navigate = useNavigate();

  const navButtons = [
    {
      id: "now-trending",
      label: "Now Trending",
      path: "/product-catalog/now-trending",
    },
    {
      id: "best-selling",
      label: "Top Selling",
      path: "/product-catalog/top-selling",
    },
    {
      id: "favorites",
      label: "Favorites",
      path: "/product-catalog/favorites",
    },
    {
      id: "all-stock",
      label: "All Stocks",
      path: "/product-catalog/all-stocks",
      hideFromInternal: true,
    },
    {
      id: "brochures",
      label: "Brochures",
      onClick: openBrochureModal,
      hideFromInternal: true,
    },
  ].filter((btn) => !btn.hideFromInternal || !isInternalPage);

  const nonActiveClass = "bg-transparent border font-bold hover:!bg-[#A33434] border-[#A33434] text-[#000000] text-base";
  const activeClass = "bg-transparent font-bold border !bg-[#A33434] hover:!bg-[#A33434] border-[#A33434] text-[#fff] text-base";

  return (
    <div className="flex flex-wrap items-center gap-5 mb-5">
      {navButtons.map((btn) => (
        <Button
          key={btn.id}
          type="primary"
          className={activeButton === btn.id ? activeClass : nonActiveClass}
          shape="round"
          size="large"
          onClick={btn.onClick ? btn.onClick : () => navigate(handleRouteNav(btn.path, role_id))}
        >
          {btn.label}
        </Button>
      ))}
    </div>
  );
};

export const DropdownMenuItems = ({
  item,
  fetchData,
  setCurrentItem,
  setIsModalOpen,
  setIsLinkProductModalOpen,
  navigate,
  postApi,
  role_id,
  full_name,
  user_id,
  showPopup,
  skipOptions = [],
}) => {
  return {
    items: productsMenuItems.filter((p) => {
      if (skipOptions.includes(p.key)) {
        return false;
      }

      if (
        role_id !== roleIds.SalesManager &&
        role_id !== roleIds.Admin &&
        ["editImage", "addToNewlyAdded", "removeFromNewlyAdded", "addToBestSelling", "removeFromBestSelling", "linkProducts"].includes(p.key)
      ) {
        return false;
      }

      if (item?.product?.is_newly && p.key === "addToNewlyAdded") {
        return false;
      }
      if (!item?.product?.is_newly && p.key === "removeFromNewlyAdded") {
        return false;
      }
      if (item?.product?.is_bestseller && p.key === "addToBestSelling") {
        return false;
      }
      if (!item?.product?.is_bestseller && p.key === "removeFromBestSelling") {
        return false;
      }

      return true;
    }),
    onClick: async ({ key, domEvent }) => {
      domEvent.stopPropagation();

      let apiRoute = "";
      let body = {};
      switch (key) {
        case "addToNewlyAdded":
          apiRoute = "sales/product/add-is-newly";
          body.is_newly = 1;
          break;
        case "removeFromNewlyAdded":
          apiRoute = "sales/product/add-is-newly";
          body.is_newly = 0;
          body.sort_newly = 0;
          break;
        case "addToBestSelling":
          apiRoute = "sales/product/add-is-bestseller";
          body.is_bestseller = 1;
          break;
        case "removeFromBestSelling":
          apiRoute = "sales/product/add-is-bestseller";
          body.is_bestseller = 0;
          break;
        case "editImage":
          setCurrentItem(item?.product);
          setIsModalOpen(true);
          break;
        case "updateShades":
          navigate(handleRouteNav("/product-catalog/shades", role_id), {
            state: {
              sort_no: item?.product?.sort_no,
              short_name: item?.short_name,
              clienttype: item?.clienttype,
              company_code: item?.company_code,
            },
          });
          break;
        case "updateStock":
          navigate(handleRouteNav("/product-catalog/stocks", role_id), {
            state: {
              sort_no: item?.product?.sort_no,
              short_name: item?.short_name,
              clienttype: item?.clienttype,
              company_code: item?.company_code,
            },
          });

        case "linkProducts":
          if (!item?.product?.sort_no || !item?.product.quality_code) {
            return showPopup({
              title: null,
              content: <span className="custom-modal-title">Add product image first!</span>,
              onOk: () => console.log("Confirmed!"),
              className: "custom-success-modal",
            });
          }

          setCurrentItem(item);
          setIsLinkProductModalOpen(true);
          break;
        default:
          break;
      }

      if (!apiRoute) {
        console.log("No API route found");
        return;
      }

      if (!item?.product?.sort_no || !item?.product.quality_code) {
        return showPopup({
          title: null,
          content: <span className="custom-modal-title">Add product image first!</span>,
          onOk: () => console.log("Confirmed!"),
          className: "custom-success-modal",
        });
      }

      // 🔹 For newly added & bestseller → find latest sort index
      const fetchSortIndex = async (route, field) => {
        try {
          const response = await postApi(route);
          if (response?.data && Array.isArray(response.data)) {
            const values = response.data.map((p) => p.product?.[field]).filter((v) => v !== undefined);
            return values?.length > 0 ? Math.max(...values) + 1 : 1;
          }
        } catch {}
        return 1;
      };

      if (key === "addToNewlyAdded") {
        body.sort_newly = await fetchSortIndex("sales/product/newly/list", "sort_newly");
      }
      if (key === "addToBestSelling") {
        body.sort_bestseller = await fetchSortIndex("sales/product/best-seller/list", "sort_bestseller");
      }

      try {
        const response = await postApi(apiRoute, {
          updated_by: full_name,
          updated_by_id: user_id,
          sort_no: item?.product?.sort_no,
          quality_code: item?.product?.quality_code,
          ...body,
        });
        fetchData();
      } catch (error) {
        console.log("error in API:", error);
      }
    },
  };
};

const Products = () => {
  const { appliedFilters, DrawerUI, FilterButton } = Filters({
    filters: [
      {
        type: "select",
        key: "category",
        label: "Category",
        apiConfig: {
          apiRoute: "sales/category/list",
          handler: (response) => {
            const { data = [] } = response;
            return data.map((item) => ({
              label: item.category_clienttype,
              value: item.category_clienttype,
            }));
          },
          method: "GET",
        },
      },
      {
        type: "select",
        key: "fabric_type",
        label: "Fabric Type",
        options: [
          { label: "SHIRTING", value: "SHIRTING" },
          { label: "SUITING", value: "SUITING" },
        ],
      },
      {
        type: "select",
        key: "blend_name",
        label: "Blend",
        apiConfig: {
          apiRoute: "sales/common/get-blend-name-list",
          handler: (response) => {
            const { data = {} } = response;
            return data.blend_name?.map((item) => ({
              label: item,
              value: item,
            }));
          },
          method: "GET",
        },
      },
      {
        type: "checkbox",
        key: "inStock",
        label: "In Stock",
      },
      {
        type: "checkbox",
        key: "inGreyStock",
        label: "Grey Available",
        className: "!mb-[28px]",
      },
      // {
      //   type: "range",
      //   key: "price",
      //   label: "Price Range",
      //   min: 0,
      //   max: 300,
      //   step: 1,
      //   marks: {
      //     0: "₹0",
      //     50: "₹50",
      //     100: "₹100",
      //     150: "₹150",
      //     200: "₹200",
      //     300: "₹300",
      //   },
      //   formatter: (val) => `₹${val}`,
      // },
      {
        type: "select",
        key: "width",
        label: "Width",
        options: [
          { label: '36"', value: "36" },
          { label: '54"', value: "54" },
          { label: '58"', value: "58" },
        ],
      },
      {
        type: "select",
        key: "favCount",
        label: "Favorite Count",
        options: [
          { label: "0", value: "0" },
          { label: "1 - 2", value: "1-2" },
          { label: "3 - 4", value: "3-4" },
          { label: "5 - 10", value: "5-10" },
          { label: "> 10", value: "10+" },
        ],
      },
    ],
    onChange: () => resetPage(),
  });

  const { showPopup } = useGlobalModal();
  const { postApi, getApi, loading } = useApiCall();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isLinkProductModalOpen, setIsLinkProductModalOpen] = useState(false);
  const [isBrochureModalOpen, setIsBrochureModalOpen] = useState(false);
  const navigate = useNavigate();
  const [productData, setProductData] = useState([]);
  const [allProducts, setAllProducts] = useState([]);
  const handleChange = (paginationInfo, filters, sorter) => {
    handlePaginationChange(paginationInfo, filters, sorter);
  };

  const user_id = useSelector((state) => state.auth.user_id);
  const full_name = useSelector((state) => state.auth.full_name);
  const { role_id, is_customer } = useSelector((state) => state.auth);
  const [searchParams, setSearchParams] = useSearchParams();
  const [currentItem, setCurrentItem] = useState(null);

  const query = searchParams.get("q") || "";
  const { pagination, setPagination, handleChange: handlePaginationChange, resetPage } = usePagination();
  const [totalProducts, setTotalProduct] = useState(0);
  const fetchIdRef = useRef(0);

  const fetchProducts = useCallback(async () => {
    fetchIdRef.current += 1;
    const currentFetchId = fetchIdRef.current;
    try {
      const { inStock, inGreyStock, width, price, category, isWishlist, favCount, fabric_type, blend_name } = appliedFilters;

      const filters = {
        inStock: inStock ? "yes" : undefined,
        inGreyStock: inGreyStock ? "yes" : undefined,
        isWishlist: isWishlist ? "yes" : undefined,
        finish_width: width,
        minRate: price?.[0],
        maxRate: price?.[1],
        clienttype: category,
        wishlistRange: favCount,
        fabric_type,
        blend_name,
      };

      const response = await postApi("sales/product/list", {
        search: query,
        per_page: pagination.pageSize,
        page: pagination.current,
        deviceType: "Web",
        ...(Object.keys(appliedFilters).length ? filters : {}),
      });

      if (currentFetchId !== fetchIdRef.current) {
        return;
      }

      const formattedRows = (response?.data || []).map((item) => ({
        ...item,
        productImageUrl: item.product?.product_pic_url,
      }));
      setProductData(formattedRows);
      setTotalProduct(response?.total || 0);
    } catch (error) {
      if (currentFetchId !== fetchIdRef.current) {
        return;
      }
      console.error("Error fetching products:", error);
    }
  }, [appliedFilters, pagination.current, pagination.pageSize, query]);

  useEffect(() => {
    const handler = setTimeout(() => {
      fetchProducts();
    }, 300);

    return () => clearTimeout(handler);
  }, [fetchProducts]);

  const fetchAllProducts = async () => {
    try {
      const response = await getApi("sales/product/get-product-name");
      const allProducts = response?.data || [];
      setAllProducts(allProducts?.filter((p) => p.product_pic_url) || []);
    } catch (error) {
      console.error("Error fetching all products:", error);
    }
  };

  useEffect(() => {
    fetchAllProducts();
  }, []);

  const handleLinkProduct = async (selectedSortNo) => {
    if (!currentItem) return;
    if (!selectedSortNo) {
      setIsLinkProductModalOpen(false);
      return;
    }

    try {
      const response = await postApi(
        "sales/product/product-linking-pair",
        {
          sort_no: currentItem.sort_no,
          sort_no_linked: selectedSortNo,
        },
        true,
      );

      if (response?.status) {
        setIsLinkProductModalOpen(false);
        fetchProducts();
      }
    } catch (error) {
      console.error("Error linking product:", error);
    }
  };

  const handleRowClick = (record) => {
    navigate(handleRouteNav(`/product-details/${record.sort_no}`, role_id));
  };

  const columns = [
    {
      title: "Product",
      dataIndex: "product",
      key: "product",
      render: (item, row) => {
        return (
          <ProductImageName
            img_url={item?.product_pic_url}
            img_alt={item?.short_name}
            text={
              <div>
                <div className="font-semibold">{row?.short_name}</div>
                <div className="text-xs text-gray-500">{row?.clienttype}</div>
              </div>
            }
            btnOnClick={(e) => {
              e.stopPropagation();
              setCurrentItem(row);
              setIsModalOpen(true);
            }}
          />
        );
      },
      ellipsis: true,
      width: 200,
    },
    // {
    //   title: "Price",
    //   dataIndex: "rate",
    //   key: "rate",
    //   render: (value) => `₹${value} / meter`,
    //   ellipsis: true,
    //   width: 100,
    // },
    {
      title: "Availability",
      dataIndex: "quality_code",
      key: "quality_code",
      render: (_, item) => {
        const { key, text, variant } = getStockStatus(item.quality_shade);

        return (
          <div className="w-full flex items-center px-2 py-2">
            <CustomBadge variant={variant} text={text} />
          </div>
        );
      },
      ellipsis: true,
      width: 140,
    },
    {
      title: "Width",
      dataIndex: "finish_width",
      key: "finish_width",
      render: (value) => `${Number(value)}''`,
      ellipsis: true,
      width: 80,
    },
    {
      title: "Actions",
      key: "action",
      width: 100,
      render: (_, item) => (
        <div className="flex gap-2 items-center">
          <Button
            className="rounded-xl text-lg py-1 px-2 w-20"
            onClick={(e) => {
              e.stopPropagation();
              navigate(handleRouteNav("/product-catalog/favorites", role_id), {
                state: {
                  sort_no: item?.product?.sort_no,
                  short_name: item?.short_name,
                },
              });
            }}
          >
            <FaHeart className="text-[#A33434] text-lg" /> {item?.product?.wishlist_count || 0}
          </Button>
          <Dropdown
            menu={DropdownMenuItems({
              item,
              fetchData: fetchProducts,
              setCurrentItem,
              setIsModalOpen,
              setIsLinkProductModalOpen,
              navigate,
              postApi,
              role_id,
              full_name,
              user_id,
              showPopup,
            })}
            trigger={["click"]}
            overlayClassName="custom-dropdown-sales-menu"
          >
            <Typography.Link
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
              }}
            >
              <Space>
                <TbDotsVertical className="text-red-700" size={18} />
              </Space>
            </Typography.Link>
          </Dropdown>
        </div>
      ),
    },
  ];

  const onSearch = (value, _e, info) => console.log(info === null || info === void 0 ? void 0 : info.source, value);

  const handleOnSearchChange = (e) => {
    const value = e.target.value;
    setSearchParams(
      (prev) => {
        if (value) prev.set("q", value);
        else prev.delete("q");
        return prev;
      },
      { replace: true },
    );
    resetPage();
  };

  const handleUpload = async (files) => {
    if (!files?.length) {
      showPopup({
        title: null,
        content: <span className="custom-modal-title">Please select an image file to upload.</span>,
        onOk: () => console.log("Confirmed!"),
        className: "custom-success-modal",
      });
      return;
    }
    const file = files[0]?.originFileObj || files[0];
    if (file.type === "image/jpg") file.type = "image/jpeg";

    const validTypes = ["image/jpeg", "image/png"];
    if (!validTypes.includes(file.type)) {
      showPopup({
        title: null,
        content: <span className="custom-modal-title">Please upload only JPG or PNG files.</span>,
        onOk: () => console.log("Confirmed!"),
        className: "custom-success-modal",
      });
      return;
    }

    const formData = new FormData();
    formData.append("quality_code", String(currentItem?.quality_code || ""));
    formData.append("sort_no", String(currentItem?.sort_no || ""));
    formData.append("updated_by", String(full_name || ""));
    formData.append("updated_by_id", String(user_id || ""));
    formData.append("product_pic", file);
    try {
      const response = await postApi("sales/product/update", formData);

      console.log("Upload successful", response.data);
      setIsModalOpen(false);
      if (response && response.status) {
        showPopup({
          title: null,
          content: <span className="custom-modal-title">{response.message}</span>,
          onOk: () => console.log("Confirmed!"),
          className: "custom-success-modal",
        });
      }
      fetchProducts();
    } catch (error) {
      console.error("Upload failed:", error.response?.data || error.message);
    }
  };

  const { setTitle, setDescription, setBackButton } = useOutletContext();

  useEffect(() => {
    if (!is_customer) {
      setTitle("Product Catalogue");
      setDescription(`${totalProducts} total products`);
      setBackButton(false);
    }
  }, [setTitle, setDescription, setBackButton, totalProducts]);

  return (
    <>
      <DrawerUI />
      <>
        <div className="bg-[#FFFFFF]" style={{ borderRadius: "32px", overflow: "hidden" }}>
          <div className="flex justify-between gap-7 items-center px-8 py-6">
            <SearchInput onSearch={onSearch} value={query} onChange={handleOnSearchChange} placeholder="Search for products" />
            <div className="flex gap-3 items-center">
              <FilterButton />
            </div>
          </div>
          {!is_customer && (
            <div className="ps-8">
              <ProductMorePagesButtons
                role_id={role_id}
                openBrochureModal={() => {
                  setIsBrochureModalOpen(true);
                }}
                isInternalPage={false}
              />
            </div>
          )}
          {loading ? (
            <div className="flex justify-center items-center py-20">
              <Loader size={60} />{" "}
            </div>
          ) : (
            <DataTable
              columns={columns}
              data={productData}
              handleChange={handleChange}
              handleRow={handleRowClick}
              pagination={{
                current: pagination.current,
                pageSize: pagination.pageSize,
                total: totalProducts,
                showSizeChanger: true,
              }}
            />
          )}
        </div>
      </>
      <BrocuresModal isOpen={isBrochureModalOpen} setIsOpen={setIsBrochureModalOpen} onSubmit={fetchProducts} />
      <FileUploadModal
        open={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
        }}
        onUpload={handleUpload} // Using the function here
        title="Upload Image"
        fileTypes={[".jpg", ".jpeg", ".png"]}
        buttonText="Update Image"
        loading={loading}
        fileTypeLabel="JPG or PNG"
      />

      <ProductLinkModal
        isOpen={isLinkProductModalOpen}
        setIsOpen={setIsLinkProductModalOpen}
        onSubmit={handleLinkProduct}
        productData={allProducts.filter((p) => p.dyed_type !== currentItem?.dyed_type)}
        onClose={() => {
          setIsLinkProductModalOpen(false);
        }}
        currentItem={currentItem}
        selectedproduct={currentItem?.product?.sort_no_linked}
        loading={loading}
      />
    </>
  );
};

export default Products;
